import { Box, Divider, Grid, OutlinedInput } from '@material-ui/core';
import { addHours, format, getTime } from 'date-fns';
import React, { useEffect, useReducer } from 'react';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';

import DangerButton from '../../components/button/Danger';
import Button from '../../components/button/Default';
import { getLaunchProduct, updateLaunchProduct } from '../../services/product.service';
import LaunchesHeader from './LaunchHeader';

const defaultState = {
    loading: true,
    product: undefined,
};

const launchProductReducer = (state = defaultState, { type, payload }) => {
    switch (type) {
        case 'COMPLETE_LAUNCH_SUCCESS':
            return { ...state, product: payload, loading: false };
        case 'LOADED':
            return { ...state, loading: false };
        case 'FETCH_LAUNCH_REQUEST':
            return { ...state, loading: true };
        case 'FETCH_LAUNCH_SUCCESS':
            return { ...state, product: payload, loading: false };
        case 'FETCH_LAUNCH_FAILURE':
            return { ...state, product: undefined, loading: false };
        default:
            return state;
    }
};

const countProduct = product =>
    product.productOptions.size.reduce(
        ({ available, released, sold }, { soldCount = 0, stockLevel }) => ({
            available: available + stockLevel,
            released: released + stockLevel + soldCount,
            sold: sold + soldCount,
        }),
        {
            available: 0,
            released: 0,
            sold: 0,
        },
    );

const LaunchProductDashboard = ({ match }) => {
    const {
        params: { id: productID },
    } = match;

    const [state, dispatch] = useReducer(launchProductReducer, defaultState);
    useEffect(() => {
        if (productID) {
            dispatch({ type: 'FETCH_LAUNCH_REQUEST' });
            getLaunchProduct(productID)
                .then(data => dispatch({ type: 'FETCH_LAUNCH_SUCCESS', payload: data }))
                .catch(() => dispatch({ type: 'FETCH_LAUNCH_FAILURE' }));
        } else {
            dispatch({ type: 'LOADED' });
        }
    }, [productID]);

    const completeLaunch = productID => {
        updateLaunchProduct(productID, { endDate: Date.now() })
            .then(data => dispatch({ type: 'COMPLETE_LAUNCH_SUCCESS', payload: data }))
            .catch(() => console.log('Failed to complete launch'));
    };

    const { product } = state;
    // handle legacy launches that don't have an endDate set yet
    const endDate = product && (product.endDate || getTime(addHours(product.launchDate, 48)));

    return product ? (
        <Box display="flex" flexDirection="column" height="100%">
            <LaunchesHeader
                leftItems={[
                    <Link className="link-button" key="back" to="/launches">
                        <Button>Go Back</Button>
                    </Link>,
                ]}
                rightItems={[
                    <DangerButton
                        disabled={!(endDate > Date.now())}
                        key="complete"
                        onClick={() => completeLaunch(productID)}
                    >
                        {endDate > Date.now() ? 'Set as Complete' : 'Launch Completed'}
                    </DangerButton>,
                ]}
            />
            <Box flex={1} mx={6} display="flex" flexDirection="column" justifyContent="baseline">
                <h2>{product.name}</h2>
                <h6>
                    Launch date:{' '}
                    {product.launchType === 'month'
                        ? format(product.launchDate, "MMMM yyyy '- TBC'")
                        : format(product.launchDate, 'dd.MM.yy / HH:mm')}
                </h6>
            </Box>
            <Box
                flex={1}
                mx={6}
                my={2}
                display="flex"
                flexDirection="column"
                justifyContent="baseline"
            >
                <h4>Launch Results</h4>
                <Grid container spacing={2} style={{ textAlign: 'center' }}>
                    <Grid item>
                        <Grid container direction={'column'}>
                            <Grid item>Released</Grid>
                            <Grid item>
                                <h6>{countProduct(product).released}</h6>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container direction={'column'}>
                            <Grid item>Sold</Grid>
                            <Grid item>
                                <h6>{countProduct(product).sold}</h6>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container direction={'column'}>
                            <Grid item>Available</Grid>
                            <Grid item>
                                <h6>{countProduct(product).available}</h6>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Box mx={6}>
                <Divider />
            </Box>
            <Box mx={6} my={2}>
                <Grid container spacing={3}>
                    <Grid item xs={3}>
                        <Pie
                            data={{
                                labels: ['Available', 'Sold'],
                                datasets: [
                                    {
                                        data: [
                                            countProduct(product).available,
                                            countProduct(product).sold,
                                        ],
                                        backgroundColor: ['#3e4555', '#E1E4EA'],
                                    },
                                ],
                            }}
                            options={{
                                legend: {
                                    position: 'bottom',
                                },
                                title: {
                                    display: true,
                                    text: 'Total Stock',
                                },
                            }}
                            width={100}
                            height={100}
                        ></Pie>
                    </Grid>
                    <Grid item xs={9}>
                        <Grid container direction={'column'} spacing={3}>
                            <Grid item>
                                <Grid container spacing={1}>
                                    <Grid item xs={2}></Grid>
                                    <Grid item xs={2}>
                                        <h6>Size</h6>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <h6>SKU</h6>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <h6>Released</h6>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <h6>Sold</h6>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <h6>Available</h6>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {product.productOptions.size.map(
                                ({ soldCount = 0, stockLevel = 0, value, SKU }) => (
                                    <Grid item key={SKU}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={2}></Grid>
                                            <Grid item xs={2}>
                                                <OutlinedInput readOnly value={value} />
                                            </Grid>
                                            <Grid item xs={2}>
                                                <OutlinedInput readOnly value={SKU} />
                                            </Grid>
                                            <Grid item xs={1}>
                                                <OutlinedInput
                                                    readOnly
                                                    value={stockLevel + soldCount}
                                                />
                                            </Grid>
                                            <Grid item xs={1}>
                                                <OutlinedInput readOnly value={soldCount} />
                                            </Grid>
                                            <Grid item xs={1}>
                                                <OutlinedInput readOnly value={stockLevel} />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ),
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    ) : null;
};

export default LaunchProductDashboard;
